import React from 'react';

import {
  Card,
  CardContent,
  CardHeader,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {useIntl, defineMessages} from 'react-intl';
import {useQuery} from 'react-query';

import API, {Types, GroupPaginationProps} from '@wheeebo/polaris-api';
import {CircleLoader, GroupPanel, LoadError, SquareChip} from '@wheeebo/polaris-ui';

const INITIAL_ITEM_COUNT = 10;

const msgs = defineMessages({
  cellName: {
    id: 'pages.member.member.GroupListCard.cell.name',
    defaultMessage: 'Name',
  },
  cellRole: {
    id: 'pages.member.member.GroupListCard.cell.role',
    defaultMessage: 'Role',
  },
  headerTitle: {
    id: 'pages.member.member.GroupListCard.header.title',
    defaultMessage: 'Groups',
  },
  headerSubTitle: {
    id: 'pages.member.member.GroupListCard.header.subTitle',
    defaultMessage: 'List of groups this member belongs to',
  },
});

interface RowProps {
  group: Types.Group;
  onSelect: () => void;
}

function Row({group, onSelect}: RowProps) {
  return (
    <TableRow hover sx={{cursor: 'pointer'}} onClick={onSelect}>
      <TableCell>
        <GroupPanel group={group} />
      </TableCell>
      <TableCell>
        <SquareChip label={group?.membership?.role || 'NON-MEMBER'} />
      </TableCell>
    </TableRow>
  );
}

interface Props {
  memberId: Types.UUID;
  onGroupSelected: (groupId: Types.UUID) => void;
}

const GroupListCard = ({memberId, onGroupSelected}: Props): JSX.Element => {
  const intl = useIntl();
  const [page, setPage] = React.useState(0);
  const options: GroupPaginationProps = {
    limit: INITIAL_ITEM_COUNT,
    offset: page * INITIAL_ITEM_COUNT,
    memberId,
    preload: ['membership'],
  };
  const {data, isError, isLoading} = useQuery(API.Groups.listKey(options), () => API.Groups.list(options));
  return (
    <Card variant="outlined">
      <CardHeader title={intl.formatMessage(msgs.headerTitle)} subheader={intl.formatMessage(msgs.headerSubTitle)} />
      <CardContent>
        {isLoading ? <CircleLoader /> : null}
        {isError ? <LoadError /> : null}
        {data && data.total > 0 ? (
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{intl.formatMessage(msgs.cellName)}</TableCell>
                  <TableCell>{intl.formatMessage(msgs.cellRole)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.items?.map((e: Types.Group) => (
                  <Row key={e.id} group={e} onSelect={() => onGroupSelected(e.id)} />
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[INITIAL_ITEM_COUNT]}
                    count={data?.total || 0}
                    rowsPerPage={INITIAL_ITEM_COUNT}
                    page={page}
                    SelectProps={{
                      inputProps: {'aria-label': 'rows per page'},
                    }}
                    onPageChange={(_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
                      setPage(newPage)
                    }
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2">No record found.</Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default GroupListCard;
